// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ApiError } from "@/api/api-error";
import {
  type CutOffTimeEntry,
  type CutOffTimesTarget,
  useDeleteCutOffTimeMutation,
} from "@/api/hooks/use-cut-off-times";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatCutOff } from "./cut-off-times";

interface DeleteCutOffTimeDialogProps {
  target: CutOffTimesTarget;
  /** The row to delete; null closes the dialog. */
  entry: CutOffTimeEntry | null;
  onClose: () => void;
  confirmDisabled?: boolean;
}

export function DeleteCutOffTimeDialog({ target, entry, onClose, confirmDisabled }: DeleteCutOffTimeDialogProps) {
  const { t } = useTranslation("back-office");
  const deleteCutOffTime = useDeleteCutOffTimeMutation(target);
  const [formError, setFormError] = useState<string | null>(null);

  // The row is cleared before the closing animation finishes, so the last one is kept for display.
  const lastEntryRef = useRef(entry);
  if (entry) lastEntryRef.current = entry;
  const shown = entry ?? lastEntryRef.current;
  const isChain = target.kind === "chain";

  useEffect(
    function clearErrorOnDialogOpen() {
      if (entry) setFormError(null);
    },
    [entry],
  );

  async function handleConfirm() {
    if (entry?.cutOffTimeId == null) return;
    setFormError(null);
    try {
      await deleteCutOffTime.mutateAsync(entry.cutOffTimeId);
      toast.success(t("stores.cutOffTimes.notifications.deleted"));
      onClose();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : t("stores.cutOffTimes.notifications.deleteFailed"));
    }
  }

  return (
    <Dialog open={!!entry} onOpenChange={() => onClose()}>
      <DialogContent
        id="dialog-delete-cut-off-time"
        className="sm:max-w-[37rem] p-0 gap-0 overflow-hidden flex flex-col"
      >
        <div className="flex flex-row">
          <WizardPane variant="destructive" icon={Trash2} />
          <div className="flex-1 p-6 flex flex-col gap-4 min-w-0">
            <DialogHeader>
              <DialogTitle>{t("stores.cutOffTimes.deleteDialog.title")}</DialogTitle>
              <DialogDescription>
                {t("stores.cutOffTimes.deleteDialog.description", {
                  time: shown ? formatCutOff(shown.cutOffTime) : "",
                  useCase: shown
                    ? t(`stores.cutOffTimes.useCases.${shown.useCase}`, { defaultValue: shown.useCase })
                    : "",
                })}
              </DialogDescription>
            </DialogHeader>

            {/* Removing a chain default silently changes behaviour for every store in the chain. */}
            {isChain && (
              <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                {t("stores.cutOffTimes.deleteDialog.chainWideWarning")}
              </p>
            )}

            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
        </div>
        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={confirmDisabled || deleteCutOffTime.isPending}
          >
            {deleteCutOffTime.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            {t("stores.cutOffTimes.deleteDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
